import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  Mic,
  ArrowLeft,
  ArrowRight,
  Users,
  Target,
  MessageSquare,
  Globe,
  CheckCircle,
  Loader2
} from 'lucide-react';
import Navbar from './shared/Navbar.jsx';
import { sharedStyles, createGlassCard, createButton } from './shared/sharedStyles.js';
import { useApi } from '../utils/api.js';

const IntakeForm = () => {
  const navigate = useNavigate();
  const { user, isSignedIn, isLoaded } = useUser();
  const { makeRequest } = useApi();

  // Consistent styling for all form elements
  const formStyles = {
    label: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.8rem',
      fontWeight: '500',
      color: sharedStyles.colors.textSecondary
    },
    input: {
      ...sharedStyles.inputStyles,
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.875rem',
      padding: '0.5rem 0.75rem'
    },
    textarea: {
      ...sharedStyles.inputStyles,
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.875rem',
      padding: '0.5rem 0.75rem',
      resize: 'vertical'
    },
    error: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.75rem',
      color: sharedStyles.colors.error,
      marginTop: '0.25rem'
    }
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Podcast Details
    podcastName: '',
    podcastDescription: '',
    podcastCategory: '',
    episodeLength: '',
    releaseFrequency: '',
    audienceSize: '',

    // Host Information
    hostName: '',
    hostBio: '',
    hostExpertise: '',

    // Guest Preferences
    guestTopics: '',
    guestExpertise: '',
    guestType: [],  // Changed to array for multi-select
    guestLocation: '',
    
    // Interview Details
    interviewFormat: '',
    recordingMethod: '',
    timeline: '',
    
    // Additional Requirements
    additionalRequirements: '',
    contactPreference: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalSteps = 5;

  // Pre-flight check: Verify subscription before allowing form access (production only)
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isLoaded || !isSignedIn) return;

      // Only enforce in production (skip in development)
      if (import.meta.env.PROD) {
        try {
          const userData = await makeRequest('users/me');
          const hasSubscription = userData.subscription_tier && (
            userData.is_lifetime || userData.stripe_subscription_id
          );

          if (!hasSubscription) {
            alert('Please subscribe to generate reports.');
            navigate('/#pricing');
          }
        } catch (error) {
          console.error('Error checking subscription:', error);
        }
      }
    };

    checkSubscription();
  }, [isLoaded, isSignedIn, makeRequest, navigate]);

  // Check for pending onboarding after sign-in
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      const pendingData = localStorage.getItem('pendingOnboarding');
      if (pendingData) {
        try {
          const savedFormData = JSON.parse(pendingData);
          setFormData(savedFormData);
          // Don't clear localStorage yet - wait until successful submission
        } catch (error) {
          console.error('Error loading pending onboarding data:', error);
          localStorage.removeItem('pendingOnboarding');
        }
      }
    }
  }, [isLoaded, isSignedIn]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.podcastName.trim()) newErrors.podcastName = 'Podcast name is required';
        if (!formData.podcastDescription.trim()) {
          newErrors.podcastDescription = 'Podcast description is required';
        } else if (formData.podcastDescription.trim().length < 200) {
          newErrors.podcastDescription = `Description must be at least 200 characters (${formData.podcastDescription.trim().length}/200)`;
        }
        if (!formData.podcastCategory) newErrors.podcastCategory = 'Please select a category';
        break;
      case 2:
        if (!formData.hostName.trim()) newErrors.hostName = 'Host name is required';
        if (!formData.hostBio.trim()) {
          newErrors.hostBio = 'Host bio is required';
        } else if (formData.hostBio.trim().length < 100) {
          newErrors.hostBio = `Bio must be at least 100 characters (${formData.hostBio.trim().length}/100)`;
        }
        break;
      case 3:
        if (!formData.guestTopics.trim()) newErrors.guestTopics = 'Please describe your ideal guest topics';
        if (!formData.guestType || formData.guestType.length === 0) newErrors.guestType = 'Please select at least one guest type';
        break;
      case 4:
        if (!formData.interviewFormat) newErrors.interviewFormat = 'Please select interview format';
        if (!formData.timeline) newErrors.timeline = 'Please select your timeline';
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    // Redirect to sign-in if not authenticated
    if (!isLoaded || !isSignedIn) {
      // Store form data in localStorage to resume after sign-in
      localStorage.setItem('pendingOnboarding', JSON.stringify(formData));
      navigate('/sign-in?redirect_url=' + encodeURIComponent('/intake-form'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Call backend API using centralized API utility
      const data = await makeRequest('onboarding', {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      // Clear stored form data
      localStorage.removeItem('pendingOnboarding');

      // Redirect to guest report page
      navigate(`/report/${data.report_id}`);

    } catch (error) {
      console.error('Onboarding error:', error);
      alert(`Failed to create report: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  const renderProgressBar = () => (
    <div style={{
      ...createGlassCard('subtle'),
      padding: '0.75rem',
      marginBottom: '1rem'
    }}>
      <div style={sharedStyles.strokeStyles} />

      {/* Progress Bar */}
      <div style={{
        width: '100%',
        height: '4px',
        background: sharedStyles.colors.secondaryLight,
        borderRadius: '2px',
        overflow: 'hidden',
        marginBottom: '0.75rem'
      }}>
        <div
          style={{
            width: `${(currentStep / totalSteps) * 100}%`,
            height: '100%',
            background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
            borderRadius: '2px',
            transition: 'width 0.3s ease',
            boxShadow: '0px 1px 2px rgba(234, 88, 12, 0.2)'
          }}
        />
      </div>

      {/* Step Indicators */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {Array.from({ length: totalSteps }, (_, i) => (
          <div
            key={i + 1}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: currentStep >= i + 1
                ? `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`
                : sharedStyles.colors.secondaryLight,
              color: currentStep >= i + 1 ? 'white' : sharedStyles.colors.textMuted,
              fontSize: '0.65rem',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            {currentStep > i + 1 ? (
              <CheckCircle size={12} />
            ) : (
              <span>{i + 1}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div style={{
      ...createGlassCard(),
      padding: '1rem'
    }}>
      <div style={sharedStyles.strokeStyles} />

      {/* Step Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1rem'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
          color: 'white',
          marginBottom: '0.5rem',
          boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.25)'
        }}>
          <Mic size={18} />
        </div>
        <h2 style={{
          ...sharedStyles.heading,
          fontSize: '1.25rem',
          marginBottom: '0.25rem'
        }}>
          Tell us about your podcast
        </h2>
        <p style={{
          ...sharedStyles.bodyText,
          color: sharedStyles.colors.textMuted,
          fontSize: '0.8rem'
        }}>
          Help us understand your show so we can find the perfect guests
        </p>
      </div>

      {/* Form Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label htmlFor="podcastName" style={formStyles.label}>
            Podcast Name *
          </label>
          <input
            id="podcastName"
            type="text"
            style={{
              ...formStyles.input,
              borderColor: errors.podcastName ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            onFocus={(e) => {
              e.target.style.borderColor = sharedStyles.colors.secondary;
              e.target.style.boxShadow = `0 0 0 3px rgba(30, 107, 127, 0.1), inset 0px 1px 2px rgba(26, 31, 46, 0.06)`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = errors.podcastName ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight;
              e.target.style.boxShadow = `inset 0px 1px 2px rgba(26, 31, 46, 0.06)`;
            }}
            placeholder="Enter your podcast name"
            value={formData.podcastName}
            onChange={(e) => handleInputChange('podcastName', e.target.value)}
          />
          {errors.podcastName && (
            <span style={formStyles.error}>
              {errors.podcastName}
            </span>
          )}
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.375rem',
          gridColumn: '1 / -1'
        }}>
          <label htmlFor="podcastDescription" style={formStyles.label}>
            Description *
            <span style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: '400',
              fontSize: '0.75rem',
              color: sharedStyles.colors.textMuted,
              marginLeft: '0.5rem'
            }}>
              (The longer the better)
            </span>
            <span style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '0.75rem',
              color: formData.podcastDescription.length < 200 ? sharedStyles.colors.error : sharedStyles.colors.textMuted,
              marginLeft: '0.5rem'
            }}>
              • {formData.podcastDescription.length}/200 min
            </span>
          </label>
          <textarea
            id="podcastDescription"
            style={{
              ...formStyles.textarea,
              minHeight: '70px',
              borderColor: errors.podcastDescription ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            placeholder="Describe your podcast, its mission, and what makes it unique..."
            rows="3"
            value={formData.podcastDescription}
            onChange={(e) => handleInputChange('podcastDescription', e.target.value)}
          />
          {errors.podcastDescription && (
            <span style={formStyles.error}>
              {errors.podcastDescription}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label htmlFor="podcastCategory" style={formStyles.label}>
            Category *
          </label>
          <select
            id="podcastCategory"
            style={{
              ...formStyles.input,
              borderColor: errors.podcastCategory ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            value={formData.podcastCategory}
            onChange={(e) => handleInputChange('podcastCategory', e.target.value)}
          >
            <option value="">Select a category</option>
            <option value="business">Business</option>
            <option value="technology">Technology</option>
            <option value="health">Health & Wellness</option>
            <option value="education">Education</option>
            <option value="entertainment">Entertainment</option>
            <option value="news">News & Politics</option>
            <option value="sports">Sports</option>
            <option value="arts">Arts & Culture</option>
            <option value="science">Science</option>
            <option value="lifestyle">Lifestyle</option>
            <option value="other">Other</option>
          </select>
          {errors.podcastCategory && (
            <span style={formStyles.error}>
              {errors.podcastCategory}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="episodeLength" style={formStyles.label}>
            Typical Episode Length
          </label>
          <select
            id="episodeLength"
            style={formStyles.input}
            value={formData.episodeLength}
            onChange={(e) => handleInputChange('episodeLength', e.target.value)}
          >
            <option value="">Select length</option>
            <option value="15-30">15-30 minutes</option>
            <option value="30-45">30-45 minutes</option>
            <option value="45-60">45-60 minutes</option>
            <option value="60-90">60-90 minutes</option>
            <option value="90+">90+ minutes</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="releaseFrequency" style={formStyles.label}>
            Release Frequency
          </label>
          <select
            id="releaseFrequency"
            style={formStyles.input}
            value={formData.releaseFrequency}
            onChange={(e) => handleInputChange('releaseFrequency', e.target.value)}
          >
            <option value="">Select frequency</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="bi-weekly">Bi-weekly</option>
            <option value="monthly">Monthly</option>
            <option value="irregular">Irregular</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="audienceSize" style={formStyles.label}>
            Audience Size
          </label>
          <select
            id="audienceSize"
            style={formStyles.input}
            value={formData.audienceSize}
            onChange={(e) => handleInputChange('audienceSize', e.target.value)}
          >
            <option value="">Select range</option>
            <option value="0-1k">0-1,000</option>
            <option value="1k-5k">1,000-5,000</option>
            <option value="5k-10k">5,000-10,000</option>
            <option value="10k-50k">10,000-50,000</option>
            <option value="50k-100k">50,000-100,000</option>
            <option value="100k+">100,000+</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div style={{
      ...createGlassCard(),
      padding: '1.5rem'
    }}>
      <div style={sharedStyles.strokeStyles} />

      {/* Step Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
          color: 'white',
          marginBottom: '1rem',
          boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.25)'
        }}>
          <Users size={20} />
        </div>
        <h2 style={{
          ...sharedStyles.heading,
          fontSize: '1.5rem',
          marginBottom: '0.5rem'
        }}>
          About the host
        </h2>
        <p style={{
          ...sharedStyles.bodyText,
          color: sharedStyles.colors.textMuted
        }}>
          Tell us about yourself to help us find guests who align with your expertise
        </p>
      </div>

      {/* Form Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="hostName" style={formStyles.label}>
            Host Name *
          </label>
          <input
            id="hostName"
            type="text"
            style={{
              ...formStyles.input,
              borderColor: errors.hostName ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            placeholder="Your name"
            value={formData.hostName}
            onChange={(e) => handleInputChange('hostName', e.target.value)}
          />
          {errors.hostName && (
            <span style={formStyles.error}>
              {errors.hostName}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="hostExpertise" style={formStyles.label}>
            Your Expertise Areas
          </label>
          <input
            id="hostExpertise"
            type="text"
            style={formStyles.input}
            placeholder="e.g., Marketing, AI, Entrepreneurship"
            value={formData.hostExpertise}
            onChange={(e) => handleInputChange('hostExpertise', e.target.value)}
          />
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          gridColumn: '1 / -1'
        }}>
          <label htmlFor="hostBio" style={formStyles.label}>
            Host Bio *
            <span style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: '400',
              fontSize: '0.75rem',
              color: sharedStyles.colors.textMuted,
              marginLeft: '0.5rem'
            }}>
              (The longer the better)
            </span>
            <span style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '0.75rem',
              color: formData.hostBio.length < 100 ? sharedStyles.colors.error : sharedStyles.colors.textMuted,
              marginLeft: '0.5rem'
            }}>
              • {formData.hostBio.length}/100 min
            </span>
          </label>
          <textarea
            id="hostBio"
            style={{
              ...formStyles.textarea,
              minHeight: '80px',
              borderColor: errors.hostBio ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            placeholder="Tell us about your background, experience, and what you bring to conversations..."
            rows="3"
            value={formData.hostBio}
            onChange={(e) => handleInputChange('hostBio', e.target.value)}
          />
          {errors.hostBio && (
            <span style={formStyles.error}>
              {errors.hostBio}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div style={{
      ...createGlassCard(),
      padding: '1.5rem'
    }}>
      <div style={sharedStyles.strokeStyles} />

      {/* Step Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
          color: 'white',
          marginBottom: '1rem',
          boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.25)'
        }}>
          <Target size={20} />
        </div>
        <h2 style={{
          ...sharedStyles.heading,
          fontSize: '1.5rem',
          marginBottom: '0.5rem'
        }}>
          Ideal guest profile
        </h2>
        <p style={{
          ...sharedStyles.bodyText,
          color: sharedStyles.colors.textMuted
        }}>
          Describe the type of guests you're looking for
        </p>
      </div>

      {/* Form Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={formStyles.label}>
            Guest Type *
            <span style={{
              fontSize: '0.75rem',
              color: sharedStyles.colors.textMuted,
              marginLeft: '0.5rem'
            }}>
              (Select up to 3)
            </span>
          </label>

          {/* All Options */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.4rem',
            padding: '0.6rem',
            border: `2px solid ${errors.guestType ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight}`,
            borderRadius: '8px',
            background: '#FFFFFF',
            maxHeight: '5.2rem',
            overflow: 'hidden'
          }}>
            {[
              { value: 'entrepreneurs', label: 'Entrepreneurs' },
              { value: 'executives', label: 'Executives' },
              { value: 'experts', label: 'Industry Experts' },
              { value: 'authors', label: 'Authors' },
              { value: 'researchers', label: 'Researchers' },
              { value: 'creators', label: 'Content Creators' },
              { value: 'any', label: 'No preference' }
            ].map(option => {
              const isSelected = formData.guestType.includes(option.value);
              const isDisabled = formData.guestType.length >= 3 && !isSelected && option.value !== 'any';

              return (
                <button
                  key={option.value}
                  onClick={() => {
                    if (isSelected) {
                      // Deselect
                      handleInputChange('guestType', formData.guestType.filter(t => t !== option.value));
                    } else if (option.value === 'any') {
                      handleInputChange('guestType', ['any']);
                    } else if (formData.guestType.length < 3) {
                      const newTypes = formData.guestType.filter(t => t !== 'any');
                      handleInputChange('guestType', [...newTypes, option.value]);
                    }
                  }}
                  disabled={isDisabled}
                  style={{
                    padding: '0.3rem 0.6rem',
                    background: isSelected
                      ? `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`
                      : 'white',
                    border: `1px solid ${isSelected ? sharedStyles.colors.primary : sharedStyles.colors.secondaryLight}`,
                    borderRadius: '16px',
                    fontSize: '0.75rem',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    color: isSelected
                      ? 'white'
                      : isDisabled
                        ? sharedStyles.colors.textMuted
                        : sharedStyles.colors.textSecondary,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: isDisabled ? 0.5 : 1,
                    fontWeight: isSelected ? '500' : 'normal'
                  }}
                onMouseEnter={(e) => {
                  if (!isDisabled && !isSelected) {
                    e.target.style.background = sharedStyles.colors.primaryLight;
                    e.target.style.borderColor = sharedStyles.colors.primary;
                    e.target.style.color = sharedStyles.colors.primary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.target.style.background = 'white';
                    e.target.style.borderColor = sharedStyles.colors.secondaryLight;
                    e.target.style.color = isDisabled
                      ? sharedStyles.colors.textMuted
                      : sharedStyles.colors.textSecondary;
                  }
                }}
              >
                {option.label}
              </button>
            );
            })}
            {formData.guestType.length >= 3 && !formData.guestType.includes('any') && (
              <span style={{
                fontSize: '0.75rem',
                color: sharedStyles.colors.textMuted,
                alignSelf: 'center',
                marginLeft: '0.5rem'
              }}>
                Maximum 3 selected
              </span>
            )}
          </div>

          {errors.guestType && (
            <span style={formStyles.error}>
              {errors.guestType}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="guestLocation" style={formStyles.label}>
            Location Preference
          </label>
          <select
            id="guestLocation"
            style={formStyles.input}
            value={formData.guestLocation}
            onChange={(e) => handleInputChange('guestLocation', e.target.value)}
          >
            <option value="">No preference</option>
            <option value="local">Local/Regional</option>
            <option value="national">National</option>
            <option value="international">International</option>
            <option value="english-speaking">English-speaking countries</option>
          </select>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          gridColumn: '1 / -1'
        }}>
          <label htmlFor="guestTopics" style={formStyles.label}>
            Topics & Expertise *
          </label>
          <textarea
            id="guestTopics"
            style={{
              ...formStyles.textarea,
              minHeight: '80px',
              borderColor: errors.guestTopics ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            placeholder="Describe the topics, expertise areas, or types of conversations you want to have..."
            rows="4"
            value={formData.guestTopics}
            onChange={(e) => handleInputChange('guestTopics', e.target.value)}
          />
          {errors.guestTopics && (
            <span style={formStyles.error}>
              {errors.guestTopics}
            </span>
          )}
        </div>

      </div>
    </div>
  );

  const renderStep4 = () => (
    <div style={{
      ...createGlassCard(),
      padding: '1.5rem'
    }}>
      <div style={sharedStyles.strokeStyles} />

      {/* Step Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
          color: 'white',
          marginBottom: '1rem',
          boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.25)'
        }}>
          <MessageSquare size={20} />
        </div>
        <h2 style={{
          ...sharedStyles.heading,
          fontSize: '1.5rem',
          marginBottom: '0.5rem'
        }}>
          Interview preferences
        </h2>
        <p style={{
          ...sharedStyles.bodyText,
          color: sharedStyles.colors.textMuted
        }}>
          How do you like to conduct your interviews?
        </p>
      </div>

      {/* Form Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="interviewFormat" style={formStyles.label}>
            Interview Format *
          </label>
          <select
            id="interviewFormat"
            style={{
              ...formStyles.input,
              borderColor: errors.interviewFormat ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            value={formData.interviewFormat}
            onChange={(e) => handleInputChange('interviewFormat', e.target.value)}
          >
            <option value="">Select format</option>
            <option value="conversational">Conversational</option>
            <option value="structured">Structured Q&A</option>
            <option value="storytelling">Storytelling</option>
            <option value="debate">Debate/Discussion</option>
            <option value="educational">Educational</option>
            <option value="mixed">Mixed approach</option>
          </select>
          {errors.interviewFormat && (
            <span style={formStyles.error}>
              {errors.interviewFormat}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="recordingMethod" style={formStyles.label}>
            Recording Method
          </label>
          <select
            id="recordingMethod"
            style={formStyles.input}
            value={formData.recordingMethod}
            onChange={(e) => handleInputChange('recordingMethod', e.target.value)}
          >
            <option value="">Select method</option>
            <option value="remote">Remote (Zoom, etc.)</option>
            <option value="in-person">In-person</option>
            <option value="phone">Phone</option>
            <option value="flexible">Flexible</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="timeline" style={formStyles.label}>
            Timeline *
          </label>
          <select
            id="timeline"
            style={{
              ...formStyles.input,
              borderColor: errors.timeline ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            value={formData.timeline}
            onChange={(e) => handleInputChange('timeline', e.target.value)}
          >
            <option value="">Select timeline</option>
            <option value="asap">ASAP</option>
            <option value="1-2-weeks">1-2 weeks</option>
            <option value="1-month">Within a month</option>
            <option value="flexible">Flexible</option>
          </select>
          {errors.timeline && (
            <span style={formStyles.error}>
              {errors.timeline}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div style={{
      ...createGlassCard(),
      padding: '1.5rem'
    }}>
      <div style={sharedStyles.strokeStyles} />

      {/* Step Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
          color: 'white',
          marginBottom: '1rem',
          boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.25)'
        }}>
          <Globe size={20} />
        </div>
        <h2 style={{
          ...sharedStyles.heading,
          fontSize: '1.5rem',
          marginBottom: '0.5rem'
        }}>
          Final details
        </h2>
        <p style={{
          ...sharedStyles.bodyText,
          color: sharedStyles.colors.textMuted
        }}>
          Any additional requirements or preferences?
        </p>
      </div>

      {/* Form Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1rem'
      }}>
<div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="contactPreference" style={formStyles.label}>
            Guest Contact Preference
          </label>
          <select
            id="contactPreference"
            style={formStyles.input}
            value={formData.contactPreference}
            onChange={(e) => handleInputChange('contactPreference', e.target.value)}
          >
            <option value="">Select preference</option>
            <option value="email">Email</option>
            <option value="linkedin">LinkedIn</option>
            <option value="social">Social Media</option>
            <option value="any">Any method</option>
          </select>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          gridColumn: '1 / -1'
        }}>
          <label htmlFor="additionalRequirements" style={formStyles.label}>
            Additional Requirements
          </label>
          <textarea
            id="additionalRequirements"
            style={{
              ...formStyles.textarea,
              minHeight: '80px'
            }}
            placeholder="Any other specific requirements, preferences, or notes..."
            rows="4"
            value={formData.additionalRequirements}
            onChange={(e) => handleInputChange('additionalRequirements', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ ...sharedStyles.pageContainer, height: '100vh', overflow: 'hidden' }}>
      {/* Header */}
      <Navbar variant="form" subtitle="Guest Discovery Form" />

      {/* Main Content */}
      <main style={{
        flex: 1,
        padding: '1rem',
        display: 'flex',
        justifyContent: 'center',
        height: 'calc(100vh - 80px)',
        overflowY: 'auto'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '800px'
        }}>
            {/* Progress Bar */}
            {renderProgressBar()}

            {/* Form Content */}
            <div style={{ marginBottom: '1rem' }}>
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
              {currentStep === 5 && renderStep5()}
            </div>

            {/* Navigation */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem'
            }}>
              {currentStep > 1 ? (
                <button
                  type="button"
                  style={{
                    ...createButton('secondary'),
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onClick={prevStep}
                >
                  <ArrowLeft size={16} />
                  Previous
                </button>
              ) : (
                <div />
              )}

              {currentStep < totalSteps ? (
                <button
                  type="button"
                  style={{
                    ...createButton('primary'),
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onClick={nextStep}
                >
                  Next
                  <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  style={{
                    ...createButton('primary'),
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      AI is discovering guests (30-60s)...
                    </>
                  ) : (
                    <>
                      Find My Guests
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              )}
            </div>
        </div>
      </main>
    </div>
  );
};

export default IntakeForm;
